import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Alert from "../../../Common/Alert";
import FormValidation from "../../../Common/FormValidation";
import Http from "../../../Common/Http";
import IsLogin from "../../../Common/IsLogin";
import useDeleteData from "../../../../hooks/useDeleteData";
import Swal from "sweetalert2";

const List = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [receiveData, setReceiveData] = useState([]);
  const [alert, setAlert] = useState(null);
  const [inputs, setInputs] = useState({
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
    status: "active",
  });
  const [salaryProcessMsg, setSalaryProcessMsg] = useState(null);
  const [salaryProcess, setSalaryProcess] = useState(null);
  const [voucherStatus, setVoucherStatus] = useState(null);
  const [error, setError] = useState({});
  const [loading, setLoading] = useState(true);
  const [editingRows, setEditingRows] = useState(new Set());
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(50);
  const [search, setSearch] = useState("");
  const [holdingStaff, setHoldingStaff] = useState(new Set()); // Track which staff are being held
  const [heldStaff, setHeldStaff] = useState(new Set()); // Track which staff are already held

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await Http.post("/salary", inputs);
      const data = res.data.result || [];

      setReceiveData(data);

      // FIXED: Use the correct field names from server response
      // The server returns 'isSalaryHold' boolean and 'salary_hold' array
      const heldStaffIds = new Set(
        data
          .filter((item) => {
            // Check both the boolean field and if salary_hold array has entries for current month/year
            const hasHoldFlag = item.isSalaryHold === true;
            const hasSalaryHoldRecord =
              item.salary_hold &&
              item.salary_hold.some(
                (hold) =>
                  parseInt(hold.month) === parseInt(inputs.month) &&
                  parseInt(hold.year) === parseInt(inputs.year)
              );

            return hasHoldFlag || hasSalaryHoldRecord;
          })
          .map((item) => item.id)
      );

      console.log("Held staff IDs:", heldStaffIds);
      setHeldStaff(heldStaffIds);

      if (res.data.salaryChk === 0) {
        setSalaryProcessMsg("Salary Not Processed. Please Process Salary");
        setSalaryProcess(0);
      } else {
        setSalaryProcessMsg("");
        setSalaryProcess(1);
      }

      if (res.data.voucherStatus === 0) {
        setVoucherStatus(0);
      } else {
        setVoucherStatus(1);
      }

      setPage(1);
    } catch (error) {
      console.error("Fetch error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const showAlert = (message, type) => {
    setAlert({ message, type });
    setTimeout(() => setAlert(null), 4000);
  };

  useEffect(() => {
    if (!location.state) return;
    showAlert(location.state.message, location.state.type);
  }, [location]);

  const handleChange = (e) => {
    const name = e.target.name;
    const value = e.target.value;
    setInputs((values) => ({ ...values, [name]: value }));
  };

  const formSubmit = () => {
    setLoading(true);
    Http.post("/salary", inputs)
      .then((res) => {
        const data = res.data.result || [];
        setReceiveData(data);

        // FIXED: Use correct field names
        const heldStaffIds = new Set(
          data
            .filter((item) => {
              const hasHoldFlag = item.isSalaryHold === true;
              const hasSalaryHoldRecord =
                item.salary_hold &&
                item.salary_hold.some(
                  (hold) =>
                    parseInt(hold.month) === parseInt(inputs.month) &&
                    parseInt(hold.year) === parseInt(inputs.year)
                );

              return hasHoldFlag || hasSalaryHoldRecord;
            })
            .map((item) => item.id)
        );

        console.log("Held staff IDs:", heldStaffIds);
        setHeldStaff(heldStaffIds); // Make sure this is uncommented!

        if (res.data.salaryChk === 0) {
          setSalaryProcessMsg("Salary Not Processed. Please Process Salary ");
          setSalaryProcess(0);
        } else {
          setSalaryProcessMsg("");
          setSalaryProcess(1);
        }

        if (res.data.voucherStatus == 0) {
          setVoucherStatus(0);
        } else {
          setVoucherStatus(1);
        }

        setPage(1);
      })
      .catch((e) => console.error(e))
      .finally(() => setLoading(false));
  };

  const deleteData = useDeleteData(fetchData);

  const processVoucher = () => {
    Http.post("/process-voucher", inputs)
      .then((res) =>
        fetchData().then(() => {
          navigate("/salary", {
            state: { message: res.data.message, type: "success" },
          });
        })
      )
      .catch((e) => setError(e.response?.data?.errors || {}));
  };

  // const formProcessSubmit = () => {
  //   // Filter out held records before processing
  //   const unHeldSalaryData = receiveData.filter((item) => {
  //     const hasHoldFlag = item.isSalaryHold === true;
  //     const hasSalaryHoldRecord =
  //       item.salary_hold &&
  //       item.salary_hold.some(
  //         (hold) =>
  //           parseInt(hold.month) === parseInt(inputs.month) &&
  //           parseInt(hold.year) === parseInt(inputs.year)
  //       );

  //     // Return items that are NOT held
  //     return !(hasHoldFlag || hasSalaryHoldRecord);
  //   });

  //   const processInputs = {
  //     ...inputs,
  //     created_by: 1,
  //     salary_data: unHeldSalaryData, // Only process non-held records
  //   };

  //   console.log("Processing salary with data:", processInputs);

  //   Http.post("/process-salary", processInputs)
  //     .then((res) =>
  //       fetchData().then(() => {
  //         navigate("/salary", {
  //           state: { message: res.data.message, type: res.data.type },
  //         });
  //       })
  //     )
  //     .catch((e) => setError(e.response?.data?.errors || {}));
  // };

  const formProcessSubmit = () => {
    // Include all records but add is_held_up flag based on hold status
    const salaryDataWithHoldStatus = receiveData.map((item) => {
      const hasHoldFlag = item.isSalaryHold === true;
      const hasSalaryHoldRecord =
        item.salary_hold &&
        item.salary_hold.some(
          (hold) =>
            parseInt(hold.month) === parseInt(inputs.month) &&
            parseInt(hold.year) === parseInt(inputs.year)
        );

      // Determine if item is held
      const isHeld = hasHoldFlag || hasSalaryHoldRecord;

      return {
        ...item,
        is_held_up: isHeld ? 1 : 0,
      };
    });

    const processInputs = {
      ...inputs,
      created_by: 1,
      salary_data: salaryDataWithHoldStatus, // Send all records with hold status
    };

    console.log("Processing salary with data:", processInputs);

    Http.post("/process-salary", processInputs)
      .then((res) =>
        fetchData().then(() => {
          navigate("/salary", {
            state: { message: res.data.message, type: res.data.type },
          });
        })
      )
      .catch((e) => setError(e.response?.data?.errors || {}));
  };

  // const handleProcessSalary = () => {
  //   const heldCount = receiveData.filter((item) => item.salaryHold).length;
  //   const processingCount = receiveData.length - heldCount;

  //   Swal.fire({
  //     title: "Process Salary Confirmation",
  //     text: `Are you sure you want to process salary for ${getMonthName(
  //       inputs.month
  //     )} ${inputs.year}? ${processingCount} records will be processed${
  //       heldCount > 0 ? ` (${heldCount} held records will be skipped)` : ""
  //     }.`,
  //     icon: "warning",
  //     showCancelButton: true,
  //     confirmButtonColor: "#3085d6",
  //     cancelButtonColor: "#d33",
  //     confirmButtonText: "Yes, Process Salary!",
  //     cancelButtonText: "Cancel",
  //   }).then((result) => result.isConfirmed && formProcessSubmit());
  // };

  const handleProcessSalary = () => {
    // Calculate held and processing counts
    const heldCount = receiveData.filter((item) => {
      const hasHoldFlag = item.isSalaryHold === true;
      const hasSalaryHoldRecord =
        item.salary_hold &&
        item.salary_hold.some(
          (hold) =>
            parseInt(hold.month) === parseInt(inputs.month) &&
            parseInt(hold.year) === parseInt(inputs.year)
        );
      return hasHoldFlag || hasSalaryHoldRecord;
    }).length;

    const processingCount = receiveData.length - heldCount;
    const totalCount = receiveData.length;

    Swal.fire({
      title: "Process Salary Confirmation",
      text: `Are you sure you want to process salary for ${getMonthName(
        inputs.month
      )} ${
        inputs.year
      }? ${totalCount} records will be sent (${processingCount} will be processed${
        heldCount > 0 ? `, ${heldCount} marked as held` : ""
      }).`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, Process Salary!",
      cancelButtonText: "Cancel",
    }).then((result) => result.isConfirmed && formProcessSubmit());
  };

  // New function to handle salary hold
  const handleSalaryHold = async (staffId) => {
    try {
      setHoldingStaff((prev) => new Set(prev).add(staffId));

      const holdData = {
        hr_staff_basic_info_id: staffId.toString(),
        month: inputs.month.toString().padStart(2, "0"),
        year: inputs.year.toString(),
        date: new Date().toISOString().split("T")[0],
      };

      console.log("Sending hold data:", holdData);

      const response = await Http.post("/salary-hold", holdData);

      // Update the receiveData to reflect the hold status
      setReceiveData((prevData) =>
        prevData.map((item) =>
          item.id === staffId
            ? {
                ...item,
                isSalaryHold: true, // Update the correct field
                salary_hold: [
                  ...(item.salary_hold || []),
                  {
                    id: response.data.id || Date.now(), // Use response ID or fallback
                    hr_staff_basic_info_id: staffId,
                    month: inputs.month.toString(),
                    year: inputs.year,
                    date: new Date().toISOString(),
                    created_at: new Date().toISOString(),
                  },
                ],
              }
            : item
        )
      );

      // Add to held staff set
      setHeldStaff((prev) => new Set(prev).add(staffId));

      showAlert(
        response.data.message || "Salary held successfully!",
        "success"
      );
    } catch (error) {
      console.error("Salary hold error:", error);
      // ... rest of your error handling code
    } finally {
      setHoldingStaff((prev) => {
        const newSet = new Set(prev);
        newSet.delete(staffId);
        return newSet;
      });
    }
  };

  const getMonthName = (monthNumber) => {
    const months = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];
    return months[monthNumber - 1] || "Unknown";
  };

  const handleEditToggle = (itemId) => {
    setEditingRows((prev) => {
      const newSet = new Set(prev);
      newSet.has(itemId) ? newSet.delete(itemId) : newSet.add(itemId);
      return newSet;
    });
  };

  const calculateDeclaredGrossSalary = (item) => {
    const basic = parseFloat(
      getFieldValue(item, "salary_structure_latest.basic") || 0
    );
    const houseRent = parseFloat(
      getFieldValue(item, "salary_structure_latest.house_rent") || 0
    );
    const medical = parseFloat(
      getFieldValue(item, "salary_structure_latest.medical") || 0
    );
    const conveyence = parseFloat(
      getFieldValue(item, "salary_structure_latest.conveyence") || 0
    );
    const otherAllowance = parseFloat(
      getFieldValue(item, "salary_structure_latest.other_allowance") || 0
    );

    return basic + houseRent + medical + conveyence + otherAllowance;
  };

  const calculateFinalSalary = (item) => {
    const declaredGross = calculateDeclaredGrossSalary(item);
    const inactiveUserGross = parseFloat(
      getFieldValue(
        item,
        "final_gross_after_inactive_date_salary_calculation"
      ) || 0
    );
    const salaryDeduction = parseFloat(
      getFieldValue(item, "salaryDeductionForNotPresent") || 0
    );
    const arrear = parseFloat(getFieldValue(item, "arrear") || 0);
    // const overtime = parseFloat(getFieldValue(item, "overtime") || 0);
    const overtime = 0;
    const holiday = parseFloat(getFieldValue(item, "holiday") || 0);
    const nightHold = parseFloat(getFieldValue(item, "night_hold") || 0);
    const tada = parseFloat(getFieldValue(item, "tada") || 0);
    const salaryAdvance = parseFloat(
      getFieldValue(item, "salary_advance") || 0
    );
    const canteen = parseFloat(getFieldValue(item, "canteen") || 0);
    const phoneBillExtra = parseFloat(
      getFieldValue(item, "phone_bill_extra") || 0
    );
    const incomeTax = parseFloat(getFieldValue(item, "income_tax") || 0);
    const deduction = parseFloat(getFieldValue(item, "deduction") || 0);
    const bonus = parseFloat(getFieldValue(item, "bonus") || 0);

    return (
      declaredGross -
      (inactiveUserGross + salaryDeduction) +
      (arrear + overtime + holiday + nightHold + tada) -
      (salaryAdvance + canteen + phoneBillExtra + incomeTax + deduction) +
      bonus
    );
  };

  const handleFieldChange = (itemId, fieldPath, value) => {
    setReceiveData((prevData) =>
      prevData.map((item) => {
        if (item.id === itemId) {
          let updatedItem = { ...item };
          if (fieldPath.includes(".")) {
            const [parentKey, childKey] = fieldPath.split(".");
            updatedItem[parentKey] = { ...item[parentKey], [childKey]: value };
          } else updatedItem[fieldPath] = value;

          const salaryComponents = [
            "salary_structure_latest.basic",
            "salary_structure_latest.house_rent",
            "salary_structure_latest.medical",
            "salary_structure_latest.conveyence",
            "salary_structure_latest.other_allowance",
          ];

          if (salaryComponents.includes(fieldPath)) {
            updatedItem.salary_structure_latest.gross =
              calculateDeclaredGrossSalary(updatedItem);
          }

          const salaryCalculationFields = [
            ...salaryComponents,
            "final_gross_after_inactive_date_salary_calculation",
            "salaryDeductionForNotPresent",
            "arrear",
            "overtime",
            "holiday",
            "night_hold",
            "tada",
            "salary_advance",
            "canteen",
            "phone_bill_extra",
            "income_tax",
            "deduction",
            "bonus",
          ];

          if (salaryCalculationFields.includes(fieldPath)) {
            updatedItem.salary = calculateFinalSalary(updatedItem);
          }

          return updatedItem;
        }
        return item;
      })
    );
  };

  const handleSaveRow = async (itemId) => {
    try {
      const itemToSave = receiveData.find((item) => item.id === itemId);
      await Http.put(`/salary/${itemId}`, itemToSave);
      setEditingRows((prev) => {
        const newSet = new Set(prev);
        newSet.delete(itemId);
        return newSet;
      });
      showAlert("Record updated successfully!", "success");
    } catch (error) {
      console.error("Save error:", error);
      showAlert("Error updating record. Please try again.", "error");
      setEditingRows((prev) => {
        const newSet = new Set(prev);
        newSet.delete(itemId);
        return newSet;
      });
    }
  };

  const formatNumber = (value) => {
    if (value === null || value === undefined || value === "") return "-";
    const num = parseFloat(value);
    return isNaN(num) ? "-" : num.toFixed(2);
  };

  const getFieldValue = (item, fieldPath) => {
    if (fieldPath.includes(".")) {
      const [parentKey, childKey] = fieldPath.split(".");
      return item[parentKey] ? item[parentKey][childKey] : "";
    }
    return item[fieldPath] || "";
  };

  const renderEditableCell = (item, fieldPath) => {
    const isEditing = editingRows.has(item.id);
    let value = getFieldValue(item, fieldPath);

    if (fieldPath === "salary_structure_latest.gross")
      value = calculateDeclaredGrossSalary(item);
    if (fieldPath === "salary") value = calculateFinalSalary(item);

    const readOnlyFields = [
      "id",
      "name",
      "designation.name",
      "joining_date",
      "inactive_date",
      "salary_structure_latest.gross",
      "salary",
    ];

    const numericFields = [
      "salary_structure_latest.basic",
      "salary_structure_latest.house_rent",
      "salary_structure_latest.medical",
      "salary_structure_latest.conveyence",
      "salary_structure_latest.entertainment",
      "salary_structure_latest.other_allowance",
      "salary_structure_latest.gross",
      "final_gross_after_inactive_date_salary_calculation",
      "salaryDeductionForNotPresent",
      "arrear",
      "overtime",
      "holiday",
      "night_hold",
      "tada",
      "salary_advance",
      "canteen",
      "phone_bill_extra",
      "income_tax",
      "deduction",
      "bonus",
      "salary",
    ];

    if (!isEditing || readOnlyFields.includes(fieldPath)) {
      const displayValue = numericFields.includes(fieldPath)
        ? formatNumber(value)
        : value || "-";

      return (
        <span
          className={
            ["salary_structure_latest.gross", "salary"].includes(fieldPath)
              ? "calculated-field"
              : ""
          }
        >
          {displayValue}
        </span>
      );
    }

    if (fieldPath === "joining_date" || fieldPath === "inactive_date") {
      return (
        <input
          type="date"
          className="form-control form-control-sm"
          value={value ? value.split(" ")[0] : ""}
          onChange={(e) =>
            handleFieldChange(item.id, fieldPath, e.target.value)
          }
        />
      );
    }

    if (numericFields.includes(fieldPath)) {
      return (
        <input
          type="number"
          step="0.01"
          className="form-control form-control-sm"
          value={value || ""}
          onChange={(e) =>
            handleFieldChange(item.id, fieldPath, e.target.value)
          }
        />
      );
    }

    return (
      <input
        type="text"
        className="form-control form-control-sm"
        value={value || ""}
        onChange={(e) => handleFieldChange(item.id, fieldPath, e.target.value)}
      />
    );
  };

  // ----- SEARCH & PAGINATION -----
  const filteredData = useMemo(() => {
    if (!search.trim()) return receiveData;
    return receiveData.filter((row) =>
      [row.id, row.name, row.designation?.name, row.salary]
        .join(" ")
        .toLowerCase()
        .includes(search.toLowerCase())
    );
  }, [search, receiveData]);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(filteredData.length / perPage)),
    [filteredData.length, perPage]
  );

  const paginatedData = useMemo(() => {
    const start = (page - 1) * perPage;
    return filteredData.slice(start, start + perPage);
  }, [filteredData, page, perPage]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [totalPages, page]);

  if (loading) return <div>Loading...</div>;

  const heldButtonStyle = `
  .btn-held {
    background-color: #6c757d !important;
    border-color: #6c757d !important;
    color: #fff !important;
    cursor: not-allowed !important;
  }
  .btn-held:hover,
  .btn-held:focus,
  .btn-held:active {
    background-color: #6c757d !important;
    border-color: #6c757d !important;
    color: #fff !important;
  }
`;

  return (
    <div>
      <IsLogin />
      <div className="am-mainpanel">
        <div className="am-pagetitle">
          <h5 className="am-title">Salary Information</h5>
          {salaryProcess === 0 ? (
            <button onClick={handleProcessSalary} className="btn btn-primary">
              <i className="fa fa-plus-square mg-r-6"></i>Process Salary
            </button>
          ) : voucherStatus === 0 ? (
            <button className="btn btn-primary" onClick={processVoucher}>
              <i className="fa fa-plus-square mg-r-6"></i>Process Voucher
            </button>
          ) : (
            ""
          )}
        </div>

        <div className="am-pagebody">
          <FormValidation error={error} />
          <Alert alert={alert} />

          <div className="card pd-sm-40">
            <div className="row">
              <div className="col-lg">
                <select
                  className="form-control"
                  name="year"
                  value={inputs.year}
                  onChange={handleChange}
                >
                  <option value="">Select Year</option>
                  {[
                    2025, 2026, 2027, 2028, 2029, 2030, 2031, 2032, 2033, 2034,
                    2035,
                  ].map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-lg">
                <select
                  className="form-control"
                  name="month"
                  value={inputs.month}
                  onChange={handleChange}
                >
                  {Array.from({ length: 12 }, (_, i) => (
                    <option key={i + 1} value={i + 1}>
                      {
                        [
                          "January",
                          "February",
                          "March",
                          "April",
                          "May",
                          "June",
                          "July",
                          "August",
                          "September",
                          "October",
                          "November",
                          "December",
                        ][i]
                      }
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-lg">
                <button
                  className="btn btn-primary btn-block mg-b-10"
                  onClick={formSubmit}
                >
                  <i className="fa fa-send mg-r-10"></i> Search
                </button>
              </div>
            </div>
          </div>

          {/* ===== TABLE ===== */}
          {salaryProcessMsg && (
            <div className="card pd-20 pd-sm-40">
              <p className="text-center text-danger">{salaryProcessMsg}</p>
            </div>
          )}

          <div className="card pd-20 pd-sm-40">
            {/* NEW: Search Input */}
            <div className="row mt-3">
              <div className="col-lg-3">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search by ID, Name, Designation..."
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                />
              </div>
              <div className="col-lg-3">
                <select
                  className="form-control"
                  value={perPage}
                  onChange={(e) => setPerPage(parseInt(e.target.value, 10))}
                >
                  {[25, 50, 100, 200].map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="table-responsive mt-3">
              <table className="table table-striped table-bordered">
                <thead>
                  <tr>
                    <th>CARD NO</th>
                    <th>NAME</th>
                    <th>DESIGNATION</th>
                    <th>JOINING DATE</th>
                    <th>BASIC</th>
                    <th>GROSS SALARY</th>
                    <th>GROSS SALARY (IF INACTIVE USER)</th>
                    <th>SALARY DEDUCTION (NOT PRESENT, LATE)</th>
                    <th>ARREAR</th>
                    {/* <th>OVERTIME</th> */}
                    <th>SALARY ADVANCE</th>
                    <th>DEDUCTION</th>
                    <th>BONUS</th>
                    <th>SALARY</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedData.map((item, index) => {
                    const isEditing = editingRows.has(item.id);
                    const isHolding = holdingStaff.has(item.id);

                    // FIXED: Check both server fields for held status
                    const isHeld =
                      item.isSalaryHold === true ||
                      heldStaff.has(item.id) ||
                      (item.salary_hold &&
                        item.salary_hold.some(
                          (hold) =>
                            parseInt(hold.month) === parseInt(inputs.month) &&
                            parseInt(hold.year) === parseInt(inputs.year)
                        ));

                    return (
                      <tr
                        key={item.id || index}
                        className={`${isEditing ? "table-warning" : ""} ${
                          isHeld ? "table-secondary held" : ""
                        }`}
                        style={isHeld ? { opacity: 0.8 } : {}}
                      >
                        <td>{renderEditableCell(item, "card_no")}</td>
                        <td>{renderEditableCell(item, "name")}</td>
                        <td>{renderEditableCell(item, "designation.name")}</td>
                        <td>{renderEditableCell(item, "joining_date")}</td>
                        <td>
                          {renderEditableCell(
                            item,
                            "salary_structure_latest.basic"
                          )}
                        </td>
                        <td>
                          {renderEditableCell(
                            item,
                            "salary_structure_latest.gross"
                          )}
                        </td>
                        <td>
                          {renderEditableCell(
                            item,
                            "final_gross_after_inactive_date_salary_calculation"
                          )}
                        </td>
                        <td>
                          {renderEditableCell(
                            item,
                            "salaryDeductionForNotPresent"
                          )}
                        </td>
                        <td>{renderEditableCell(item, "arrear")}</td>
                        {/* <td>{renderEditableCell(item, "overtime")}</td> */}
                        <td>{renderEditableCell(item, "salary_advance")}</td>
                        <td>{renderEditableCell(item, "deduction")}</td>
                        <td>{renderEditableCell(item, "bonus")}</td>
                        <td>{renderEditableCell(item, "salary")}</td>
                        <td className="text-xs">
                          {salaryProcess == 0 ? (
                            <button
                              type="button"
                              className={`btn btn-sm ${
                                isHeld ? "btn-held" : "btn-danger"
                              }`}
                              onClick={() => handleSalaryHold(item.id)}
                              disabled={isHolding || isHeld}
                            >
                              {isHolding ? (
                                <>
                                  <span
                                    className="spinner-border spinner-border-sm mr-1"
                                    role="status"
                                    aria-hidden="true"
                                  ></span>
                                  Holding...
                                </>
                              ) : isHeld ? (
                                "Held"
                              ) : (
                                "Hold Up"
                              )}
                            </button>
                          ) : isHeld ? (
                            "Held"
                          ) : (
                            // <Link
                            //   to={{ pathname: "/pay-slip/" + item.salary_id }}
                            //   className="btn btn-primary btn-sm"
                            // >
                            //   Pay Slip
                            // </Link>
                            "Process Done"
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="d-flex justify-content-between align-items-center mt-3">
              <div>
                Showing {paginatedData.length} of {filteredData.length} entries
                {receiveData.filter((item) => item.salaryHold).length > 0 && (
                  <span className="text-muted ml-2">
                    ({receiveData.filter((item) => item.salaryHold).length}{" "}
                    held)
                  </span>
                )}
              </div>
              <div>
                <button
                  className="btn btn-sm btn-outline-primary mr-1"
                  disabled={page === 1}
                  onClick={() => setPage((p) => p - 1)}
                >
                  Prev
                </button>
                <span>
                  {" "}
                  Page {page} of {totalPages}{" "}
                </span>
                <button
                  className="btn btn-sm btn-outline-primary ml-1"
                  disabled={page === totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default List;
